import { useQueries, useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { sortBy } from 'lodash';
import React, { useEffect } from 'react';
import type { RegisterOptions } from 'react-hook-form';

import { isValidUUID } from '@/features/SourceCreate/SourceCreate.utils';
import { isCustomerCloudSourcePageAtom } from '@/pages/Administration/Devices/Devices.store';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import { axiosClient } from '@/services/BaseApi.service';
import EncryptData from '@/utils/SecurityHelper';

import type { LimaDetailedSourceDTO } from '../DevicesTable/DevicesTable.types';
import SOURCE_TYPE, {
  CATEGORY_SUPPORTED_SOURCE_TYPES,
  prefixMsgConfig,
  SOURCE_TYPES_WITH_INFO_BLOCK
} from './SourceCreate.constants';
import {
  categoryAtom,
  connectionInputSchemaAtom,
  limaDevicesAtom,
  microsoftDataProvidersCategoriesAtom,
  originalSourceTypesAtom,
  selectedIngestConfigTabAtom,
  selectedSourceDetailsAtom,
  selectedSourceTypeIdAtom,
  sourceLogTypeAtom,
  sourceTypeBasicAtom,
  supportedLogSourcesAtom,
  tieredSourceTypesAtom
} from './SourceCreate.store';
import {
  type BasicSourceLogTypeDTO,
  type BasicSourceTypeDTO,
  type ConnectionInputSchema,
  type DetailedSourceTypeDTO,
  type LimaDevicesDTO,
  type SourceDetailResponse,
  SourceDialogMode,
  type Validation
} from './SourceCreate.types';
import type { SupportedLogSourceModel } from './SourceInfoBlock/SourceInfoBlock.types';

/**
 * Merges categories from multiple Microsoft data provider query results.
 * Pure function for easier testing and reusability.
 *
 * @param queries - Array of React Query results
 * @returns Merged array of categories from all successful queries
 */
const mergeMicrosoftCategories = (
  queries: Array<{ data: unknown; isSuccess: boolean }>
): BasicSourceLogTypeDTO[] => {
  const mergedCategories: BasicSourceLogTypeDTO[] = [];

  queries.forEach((query) => {
    if (query.data) {
      const providerCategories = query.data as BasicSourceLogTypeDTO[];
      mergedCategories.push(...providerCategories);
    }
  });

  return mergedCategories;
};

/**
 * Converts backend validation rules to React Hook Form validation config.
 *
 * Business Rule: Backend defines validation in a generic format (type + params),
 * which must be transformed into React Hook Form's RegisterOptions format.
 *
 * @param validations - Array of validation rules from backend schema
 * @returns React Hook Form validation configuration
 */
export const createValidationRules = (
  validations: Validation[]
): RegisterOptions => {
  if (!validations || validations.length === 0) {
    return {};
  }

  const regOpt: RegisterOptions = {};
  const validateFns: Record<string, (value: string) => true | string> = {};

  validations.forEach((validation: Validation) => {
    const validationType = validation.type.toLowerCase();

    // Required field validation
    if (validationType === 'required') {
      regOpt.required = validation.params?.join(',') || true;
      return;
    }

    // Max length validation
    if (validationType === 'maxlength') {
      const maxLength = Number(validation.params?.[0] || 100);
      validateFns.maxlength = (value: string) =>
        value.length <= maxLength || `Maximum ${maxLength} characters allowed`;
      return;
    }

    // Pattern/regex validation
    if (validationType === 'pattern' && validation.params?.[0]) {
      const regex = new RegExp(validation.params[0]);
      const errorMessage = validation.params?.[1] || 'Invalid format';
      validateFns.pattern = (value: string) =>
        regex.test(value) || errorMessage;
    }
  });

  // Attach custom validators if any were created
  if (Object.keys(validateFns).length > 0) {
    regOpt.validate = validateFns;
  }

  return regOpt;
};

/**
 * Applies validation rules to all input sections of a source type schema.
 *
 * Business Context: Source forms have multiple input sections (source_inputs,
 * client_secret_inputs, certificate_inputs) that each need validation rules
 * applied. This function processes all sections consistently.
 *
 * @param sourceTypeJsonSchema - Raw schema from backend
 * @returns Schema with React Hook Form validation attached to each field
 */
export const mapDynamicFormValidationRules = (
  sourceTypeJsonSchema: ConnectionInputSchema
): ConnectionInputSchema => {
  const srcTypeJsonSchema = { ...sourceTypeJsonSchema };

  // List of input sections that need validation rules applied
  const inputSections = [
    'source_inputs',
    'client_secret_inputs',
    'existing_certificate_inputs',
    'new_certificate_inputs'
  ] as const;

  // Apply validation rules to each section if it exists
  inputSections.forEach((sectionName) => {
    const section = srcTypeJsonSchema[sectionName];
    if (section && Array.isArray(section)) {
      srcTypeJsonSchema[sectionName] = section.map((field) => ({
        ...field,
        config: createValidationRules(field.validations)
      }));
    }
  });

  return srcTypeJsonSchema;
};

/**
 * Triggers browser download of a base64-encoded file.
 *
 * @param filename - Name for the downloaded file
 * @param data - Base64-encoded file content
 */
const triggerBrowserDownload = (filename: string, data: string) => {
  const downloadLink = document.createElement('a');
  downloadLink.setAttribute('href', `data:text/plain;base64,${data}`);
  downloadLink.setAttribute('download', filename);
  downloadLink.style.display = 'none';

  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
};

/**
 * Downloads certificate file for a source.
 *
 * Business Context: Sources using certificate authentication need to download
 * their certificate files for use in external systems (Azure, Microsoft 365, etc.).
 * Backend encrypts the certificate content for security.
 *
 * @param sourceUid - Unique identifier for the source
 * @returns Promise that resolves when download completes
 */
export const downloadCertificate = async (sourceUid: string) => {
  try {
    const response = await axiosClient.get(
      `/lima_sources/${sourceUid}/download_certificate`
    );

    if (response?.data?.file_name && response?.data?.file_content) {
      const decryptedContent = await EncryptData.decrypt(
        response.data.file_content
      );
      triggerBrowserDownload(response.data.file_name, decryptedContent);
    }
  } catch (error) {
    // Certificate download is best-effort and should not block source workflows.
    console.error('Failed to download source certificate:', error);
  }
};

/**
 * Builds confirmation message for AWS S3 sources without prefix specified.
 *
 * Business Context: When creating AWS S3 sources without a prefix,
 * the system will ingest from default directories. Users should confirm
 * this is intentional to avoid accidental broad data ingestion.
 *
 * @param selectedProviders - List of provider keys (e.g., "CloudTrail", "VPCFlowLogs")
 * @returns Formatted confirmation message with provider-specific paths
 */
export const buildMultiProviderMessage = (selectedProviders: string[]) => {
  type ProviderKey = keyof typeof prefixMsgConfig.providers;

  // Build sections for each provider with their default paths
  const providerSections = selectedProviders
    .map((providerKey) => {
      const config = prefixMsgConfig.providers[providerKey as ProviderKey];
      if (!config) {
        return null;
      }

      // Format each path with indentation
      const formattedPaths = config.paths.map((path) => `  ${path}`).join('\n');

      return `${config.title}:\n${formattedPaths}`;
    })
    .filter(Boolean)
    .join('\n\n');

  return (
    `${prefixMsgConfig.commonPrefixText}\n\n` +
    `${providerSections}\n\n${prefixMsgConfig.commonSuffixText}`
  );
};

/**
 * Fetches source details for the source details page.
 *
 * Business Context: Microsoft sources require a different endpoint that returns
 * aggregated data provider information. Other sources use the standard endpoint
 * that returns single-source details.
 *
 * @param sourceUid - UUID of the source to fetch
 * @param sourceTypeId - Source type ID (determines which endpoint to use)
 * @returns React Query result with source details
 */
export const useFetchSourceDetailsBySourceUID = (
  sourceUid: string,
  sourceTypeId: number
) => {
  const isMicrosoftSource = sourceTypeId === SOURCE_TYPE.MICROSOFT;

  // Microsoft sources use a specialized endpoint with provider aggregation
  const url = isMicrosoftSource
    ? `lima_sources/${sourceUid}/ms_sources`
    : `lima_sources/${sourceUid}`;

  const sourceDetailQuery = useQuery<LimaDetailedSourceDTO>({
    queryKey: [url, sourceUid],
    enabled: isValidUUID(sourceUid)
  });

  return sourceDetailQuery;
};

/**
 * Decodes base64-encoded source inputs from backend response.
 *
 * Business Context: Sensitive connection details (credentials, tokens) are
 * base64-encoded by backend for transport security and must be decoded
 * before populating the edit form.
 *
 * @param sourceDetails - Raw response with encoded fields
 * @returns Decoded source details ready for form population
 */
const decodeSourceInputs = (
  sourceDetails: SourceDetailResponse
): SourceDetailResponse => {
  const decoded = { ...sourceDetails };

  // Decode source inputs if present
  if (decoded.source_inputs_encoded) {
    const decodedSourceInputs = atob(decoded.source_inputs_encoded);
    decoded.source_inputs = JSON.parse(decodedSourceInputs);
  }

  // Decode client secret inputs if present
  if (decoded.client_secret_inputs_encoded) {
    const decodedClientSecret = atob(decoded.client_secret_inputs_encoded);
    decoded.client_secret_inputs = JSON.parse(decodedClientSecret);
  }

  return decoded;
};

/**
 * Fetches and decodes source details for editing.
 *
 * Used by the Edit Source dialog to populate form fields with existing
 * source configuration. Only runs when in Edit mode with valid source UID.
 *
 * @param sourceUid - UUID of the source to fetch
 * @param mode - Dialog mode (New or Edit)
 * @returns React Query result with decoded source details
 */
export const useFetchSourceDetailBySourceUID = (
  sourceUid: string,
  mode: string
) => {
  const [, setSelectedSourceDetails] = useAtom(selectedSourceDetailsAtom);

  const sourceDetailsQuery = useQuery<SourceDetailResponse>({
    queryKey: [`lima_sources/details/${sourceUid}`],
    enabled: isValidUUID(sourceUid) && mode === SourceDialogMode.Edit
  });

  React.useEffect(() => {
    // Success: Decode and store source details
    if (sourceDetailsQuery.isSuccess && sourceDetailsQuery.data) {
      const decodedDetails = decodeSourceInputs(sourceDetailsQuery.data);
      setSelectedSourceDetails(decodedDetails);
      return;
    }

    // Error: Clear state to prevent stale data
    if (sourceDetailsQuery.isError) {
      setSelectedSourceDetails({} as SourceDetailResponse);
    }
  }, [
    sourceDetailsQuery.data,
    sourceDetailsQuery.isError,
    sourceDetailsQuery.isSuccess,
    setSelectedSourceDetails
  ]);

  return sourceDetailsQuery;
};

/**
 * Filters out OAuth-specific fields for Dropbox in New mode.
 *
 * Business Context: When creating a new Dropbox source, OAuth fields
 * (access_code, authorize_url) are auto-generated by the backend flow
 * and should not be shown in the form. In Edit mode, these fields remain
 * visible for debugging.
 *
 * @param schema - Connection input schema
 * @param mode - Dialog mode (New or Edit)
 * @param sourceTypeId - Selected source type ID
 * @returns Filtered schema
 */
const filterDropboxOAuthFields = (
  schema: ConnectionInputSchema,
  mode: string,
  sourceTypeId: number
): ConnectionInputSchema => {
  // Only apply filtering for new Dropbox sources
  if (mode !== 'New' || sourceTypeId !== SOURCE_TYPE.DROPBOX) {
    return schema;
  }

  return {
    ...schema,
    source_inputs: schema.source_inputs?.filter(
      (field) => field.name !== 'access_code' && field.name !== 'authorize_url'
    )
  };
};

/**
 * Fetches and processes source type schema.
 *
 * Retrieves the dynamic form schema for the selected source type and applies
 * validation rules. The schema defines which fields appear in the form and
 * their validation requirements.
 *
 * @param mode - Dialog mode (New or Edit)
 * @returns React Query result with processed schema
 */
export const useFetchSourceTypeSchema = (mode: string) => {
  const [, setConnectionInputSchema] = useAtom(connectionInputSchemaAtom);
  const [selectedSourceTypeId] = useAtom(selectedSourceTypeIdAtom);
  const [, setSelectedIngestConfigTab] = useAtom(selectedIngestConfigTabAtom);

  const sourceTypeSchemaQuery = useQuery<DetailedSourceTypeDTO>({
    queryKey: [`source_type/${selectedSourceTypeId}`],
    enabled: !!selectedSourceTypeId,
    staleTime: 60 * 60 * 1000 // Cache for 1 hour
  });

  React.useEffect(() => {
    if (!sourceTypeSchemaQuery.data) {
      return;
    }

    const { data } = sourceTypeSchemaQuery;

    // Guard: Ensure schema matches currently selected source type
    // (prevents race conditions during rapid source type switching)
    if (data.id !== selectedSourceTypeId) {
      return;
    }

    // Extract and process the connection input schema
    let processedSchema = {
      ...(data.connection_input_schema as ConnectionInputSchema)
    };

    // Apply source-specific filtering
    processedSchema = filterDropboxOAuthFields(
      processedSchema,
      mode,
      selectedSourceTypeId
    );

    // Apply validation rules to all fields
    processedSchema = mapDynamicFormValidationRules(processedSchema);

    setConnectionInputSchema(processedSchema);
    setSelectedIngestConfigTab('ingest_method_config');
  }, [
    sourceTypeSchemaQuery.data,
    selectedSourceTypeId,
    mode,
    setConnectionInputSchema,
    setSelectedIngestConfigTab
  ]);

  React.useEffect(() => {
    // Clear schema on error to prevent stale data
    if (sourceTypeSchemaQuery.isError) {
      setConnectionInputSchema({});
    }
  }, [sourceTypeSchemaQuery.isError, setConnectionInputSchema]);

  return sourceTypeSchemaQuery;
};

/**
 * Fetches and sorts Lima devices for device selection dropdown.
 *
 * Business Context: When creating a source, users must assign it to a Lima
 * device (log collector). This hook fetches all available devices and sorts
 * them alphabetically for easier selection.
 *
 * @returns React Query result with sorted Lima devices
 */
export const useFetchLimaDevices = () => {
  const [, setLimaDevices] = useAtom(limaDevicesAtom);

  const limaDevicesQuery = useQuery({
    queryKey: ['devices/lima_devices/all'],
    staleTime: Infinity // Lima devices rarely change, cache indefinitely
  });

  React.useEffect(() => {
    // Success: Sort devices alphabetically by name
    if (limaDevicesQuery.data) {
      const devices = limaDevicesQuery.data as LimaDevicesDTO[];
      const sortedDevices = sortBy(devices, ['name']);
      setLimaDevices(sortedDevices);
      return;
    }

    // Error: Clear devices to show empty state
    if (limaDevicesQuery.isError) {
      setLimaDevices([]);
    }
  }, [limaDevicesQuery.data, limaDevicesQuery.isError, setLimaDevices]);

  return limaDevicesQuery;
};

/**
 * Fetches supported log sources for specific source types.
 *
 * Business Context: Some cloud streaming sources can ingest logs from various
 * upstream services. This hook fetches the list of supported/tested log source
 * types to help users configure those sources correctly.
 *
 * @param sourceTypeId - Selected source type ID
 * @returns React Query result with supported log sources
 */
export const useFetchSupportedLogSources = (sourceTypeId: number) => {
  const [, setSupportedLogSources] = useAtom(supportedLogSourcesAtom);

  const isSupportedLogSource = SOURCE_TYPES_WITH_INFO_BLOCK.includes(
    sourceTypeId as (typeof SOURCE_TYPES_WITH_INFO_BLOCK)[number]
  );

  const supportedLogSourcesQuery = useQuery({
    queryKey: [`lima_supported_log_sources/display-names/${sourceTypeId}`],
    enabled: isSupportedLogSource,
    staleTime: 60 * 60 * 1000 // Cache for 1 hour
  });

  React.useEffect(() => {
    // Early exit: Non-Event Hub sources don't use this feature
    if (!isSupportedLogSource) {
      setSupportedLogSources([]);
      return;
    }

    // Success: Store supported log sources
    if (supportedLogSourcesQuery.data) {
      const logSources =
        supportedLogSourcesQuery.data as SupportedLogSourceModel[];
      setSupportedLogSources(logSources);
      return;
    }

    // Error: Clear state
    if (supportedLogSourcesQuery.isError) {
      setSupportedLogSources([]);
    }
  }, [
    supportedLogSourcesQuery.data,
    supportedLogSourcesQuery.isError,
    isSupportedLogSource,
    setSupportedLogSources
  ]);

  return supportedLogSourcesQuery;
};

/**
 * Fetches available categories (log types) for the selected source type.
 *
 * Business Context: Most sources have selectable categories/log types
 * (e.g., CloudTrail has "Management Events", "Data Events"). This hook
 * fetches available categories for the user to select. Microsoft sources
 * handle categories differently - they're managed at the data provider level.
 *
 * @param sourceTypeId - Selected source type ID
 * @returns React Query result with available categories
 */
export const useFetchSourceLogType = (sourceTypeId: number) => {
  const [, setCategories] = useAtom(categoryAtom);
  const [, setSourceLogTypeData] = useAtom(sourceLogTypeAtom);

  // Only fetch categories for source types that support them
  const shouldFetchCategories =
    sourceTypeId !== 0 &&
    CATEGORY_SUPPORTED_SOURCE_TYPES.includes(sourceTypeId);

  const categoryQuery = useQuery({
    queryKey: [`lima_source_log_types/source_types/${sourceTypeId}`],
    enabled: shouldFetchCategories,
    staleTime: 60 * 60 * 1000 // Cache for 1 hour
  });

  React.useEffect(() => {
    // Success: Store categories
    if (categoryQuery.data) {
      const categories = categoryQuery.data as BasicSourceLogTypeDTO[];
      setSourceLogTypeData(categories);
      setCategories([...categories]);
      return;
    }

    // Error: Clear categories
    if (categoryQuery.isError) {
      setCategories([]);
      setSourceLogTypeData([]);
    }
  }, [
    categoryQuery.data,
    categoryQuery.isError,
    setCategories,
    setSourceLogTypeData
  ]);

  React.useEffect(() => {
    // Clear categories when disabled (no source selected or Microsoft source)
    if (!shouldFetchCategories) {
      setCategories([]);
      setSourceLogTypeData([]);
    }
  }, [shouldFetchCategories, setCategories, setSourceLogTypeData]);

  return categoryQuery;
};

/**
 * Fetches source types organized by organization tier.
 *
 * Business Context: Source availability is gated by organization tier/subscription.
 * This hook fetches source types available to the selected organization,
 * hiding premium sources from lower-tier customers.
 *
 * @returns React Query result with tier-filtered source types
 */
export const useFetchSourceTypesByTier = () => {
  const [, setTieredSourceTypes] = useAtom(tieredSourceTypesAtom);
  const [selectedOrgData] = useAtom(selectedOrganizationAtom);

  const url = `organizations/${selectedOrgData.id}/device_types/1/source_types_by_tier`;

  const sourceTypeByTierQuery = useQuery({
    queryKey: [url],
    enabled: selectedOrgData.id > 0,
    staleTime: Infinity // Source type availability rarely changes
  });

  React.useEffect(() => {
    // Success: Sort and store available source types
    if (sourceTypeByTierQuery.data) {
      const sourceTypes = sourceTypeByTierQuery.data as BasicSourceTypeDTO[];
      const sortedSourceTypes = sortBy(sourceTypes, ['name']);
      setTieredSourceTypes(sortedSourceTypes);
      return;
    }

    // Error: Clear source types
    if (sourceTypeByTierQuery.isError) {
      setTieredSourceTypes([]);
    }
  }, [
    sourceTypeByTierQuery.data,
    sourceTypeByTierQuery.isError,
    setTieredSourceTypes
  ]);

  return sourceTypeByTierQuery;
};

/**
 * Fetches basic source type information.
 *
 * Business Context: Loads either tier-restricted source types (for customer
 * cloud source page) or all source types (for internal admin pages). This
 * determines which sources appear in the source type dropdown.
 *
 * @returns React Query result with source types
 */
export const useFetchSourceTypeBasic = (
  isCustomerCloudSourcePageOverride?: boolean
) => {
  const [, setSourceTypeBasic] = useAtom(sourceTypeBasicAtom);
  const [selectedOrgData] = useAtom(selectedOrganizationAtom);
  const [, setOriginalSourceTypes] = useAtom(originalSourceTypesAtom);
  const [isCustomerCloudSourcePage] = useAtom(isCustomerCloudSourcePageAtom);

  // Prefer caller-provided page mode so first render does not depend on atom timing.
  const isCustomerCloudSourcePageResolved =
    isCustomerCloudSourcePageOverride ?? isCustomerCloudSourcePage;

  // Use tier-filtered endpoint for customer pages, all sources for admin
  const url = isCustomerCloudSourcePageResolved
    ? `organizations/${selectedOrgData.id}/device_types/1/source_types_by_tier`
    : `organizations/${selectedOrgData.id}/device_types/1/lima_source_types`;

  // Enable query only when organization is selected (for customer pages).
  // For internal admins, all organizations are available, so no org selection is needed.
  const isEnabled = isCustomerCloudSourcePageResolved
    ? selectedOrgData.id > 0
    : true;

  const sourceTypeQuery = useQuery({
    queryKey: [url],
    enabled: isEnabled,
    staleTime: Infinity // Source types rarely change
  });

  React.useEffect(() => {
    // Success: Sort and store source types
    if (sourceTypeQuery.data) {
      const sourceTypes = sourceTypeQuery.data as BasicSourceTypeDTO[];
      const sortedSourceTypes = sortBy(sourceTypes, ['name']);

      // Store in both atoms for different use cases
      setOriginalSourceTypes(sortedSourceTypes);
      setSourceTypeBasic(sortedSourceTypes);
      return;
    }

    // Error: Clear source types
    if (sourceTypeQuery.isError) {
      setSourceTypeBasic([]);
    }
  }, [
    sourceTypeQuery.data,
    sourceTypeQuery.isError,
    setSourceTypeBasic,
    setOriginalSourceTypes
  ]);

  return sourceTypeQuery;
};

/**
 * Fetches categories for multiple Microsoft source type data providers.
 *
 * Business Context: Microsoft sources (O365, Defender, Entra ID) each have
 * their own category sets. When user selects Microsoft as the source type,
 * we need to fetch categories for all Microsoft sub-providers so users can
 * choose which data providers and categories to enable.
 *
 * Uses parallel queries for performance - fetches all provider categories
 * simultaneously rather than sequentially.
 *
 * @param sourceTypeIds - Array of Microsoft provider source type IDs
 * @param enabled - Whether to fetch (typically enabled when Microsoft is selected)
 * @returns Array of React Query results (one per provider)
 */
export const useFetchMicrosoftDataProviderCategories = (
  sourceTypeIds: number[],
  enabled: boolean
) => {
  const [, setMicrosoftCategories] = useAtom(
    microsoftDataProvidersCategoriesAtom
  );

  // Fetch categories for each provider in parallel
  const queries = useQueries({
    queries: sourceTypeIds.map((sourceTypeId) => ({
      queryKey: [`lima_source_log_types/source_types/${sourceTypeId}`],
      enabled: enabled && sourceTypeId !== 0,
      staleTime: 60 * 60 * 1000 // Cache for 1 hour
    }))
  });

  // Track aggregate query states for effect dependencies
  const resultsData = queries.map((query) => query.data);
  const hasError = queries.some((query) => query.isError);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Wait for all queries to settle before updating state
    // (prevents showing partial data during loading)
    const allSettled = queries.every(
      (query) => query.isFetched || query.isError
    );

    if (!allSettled) {
      return;
    }

    // Error case: Clear categories
    if (hasError) {
      setMicrosoftCategories([]);
      return;
    }

    // Success case: Merge categories from all providers using pure function
    const mergedCategories = mergeMicrosoftCategories(queries);
    setMicrosoftCategories(mergedCategories);
  }, [enabled, hasError, JSON.stringify(resultsData), setMicrosoftCategories]);

  return queries;
};
